import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EDIT_PARAM, editMatchId } from "../lib/matchEditLink";

/**
 * Issue #899 (supersedes D148's stopgap) — the `?edit=<matchId>` half of
 * `PerformanceTracking.jsx`'s log-a-match flow, pulled out into its own
 * hook for the same reason `hooks/useGroundsNearYou.js`'s own header gives
 * for its extraction, and the same reason issue #904's `useLogFlowStep`
 * (PR #910) exists at all: this repo measures `max-lines-per-function`
 * against `PerformanceTracking` itself (`eslint.config.mjs`'s own budget
 * header cites it by name), and this feature's state + two effects grew
 * that function past the 710-line budget in place.
 *
 * Review round 3, F6 — same "read once, strip immediately" contract
 * `activityCreate.js`'s own `?new=1` established (see that file's header):
 * the id is captured into state on the mount the deep link caused, the
 * param is stripped before `GET /matches` has even answered, and a
 * bookmarked/shared URL never re-opens a stale edit.
 *
 * `openEdit` is passed in rather than owned here — it lives on
 * `PerformanceTracking.jsx` itself (needs `reflectionsByMatch`/`setForm`/
 * etc., all page state this hook has no business owning), so this hook
 * only ever calls it once `matches` has landed and named the id.
 *
 * COORDINATION WITH #910's `useLogFlowStep` — the two hooks own two
 * DIFFERENT query params (`?edit=` here, `?step=` there) and neither
 * reads or writes the other's. `useLogFlowStep`'s own `isEditing` param
 * (`PerformanceTracking.jsx`'s `Boolean(editingId)`) already keeps IT from
 * writing `?step=` during an edit, deep-linked or not (that hook's own
 * F8) — nothing here has to ask it to. `?edit=` stays owned exclusively by
 * this hook, `?step=` exclusively by that one.
 *
 * @param {Array<{match_id: string|number}>} matches — `GET /matches`'s
 *   own list; `openEdit` needs the FULL record, not just an id, so
 *   resolution waits for this to land rather than acting on the id alone.
 * @param {(m: object) => void} openEdit — `PerformanceTracking.jsx`'s own.
 * @returns {[boolean, () => void]} `[editingViaDeepLink, endDeepLinkEdit]`
 *   — the boolean for the toast-suppression check
 *   (`if (!editingViaDeepLink) toast.success(...)`), and a function that
 *   IS the "leave a deep-linked edit session" step: a no-op when there is
 *   no session to leave, so both the save path and `closeDialog` can call
 *   it unconditionally rather than each guarding it with the same `if`.
 *   `navigate` is owned here (not threaded in) for exactly that reason —
 *   `endDeepLinkEdit` is a complete, self-contained step.
 */
export function useEditDeepLink(matches, openEdit) {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  // Lazy initial state: the param is read on the mount the deep link
  // caused. Later renders never re-derive it — by then the param is gone.
  const [pendingEditId, setPendingEditId] = useState(() => editMatchId(sp));
  const [editingViaDeepLink, setEditingViaDeepLink] = useState(false);

  useEffect(() => {
    if (!editMatchId(sp)) return;
    const next = new URLSearchParams(sp);
    next.delete(EDIT_PARAM);
    setSp(next, { replace: true });
    // Mount-only: a one-time "was I deep-linked here" strip, not a rule
    // that should re-fire on every `sp` change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolves `pendingEditId` the moment `GET /matches` actually answers.
  // An id that names no match on this account (stale link, wrong account,
  // already deleted) is silently dropped rather than erroring — the
  // player lands on the ordinary list, same as `/performance` with no
  // param at all.
  useEffect(() => {
    if (!pendingEditId || matches.length === 0) return;
    const m = matches.find((x) => String(x.match_id) === String(pendingEditId));
    setPendingEditId(null);
    if (m) {
      setEditingViaDeepLink(true);
      openEdit(m);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, pendingEditId]);

  const endDeepLinkEdit = () => {
    if (!editingViaDeepLink) return;
    setEditingViaDeepLink(false);
    navigate("/dashboard");
  };

  return [editingViaDeepLink, endDeepLinkEdit];
}

export default useEditDeepLink;
